import { Box } from '@mui/material'
import { AppV1Appsowner } from 'c1gen/roles'
import { C1ChipProps } from 'c1ui/components/C1Chip'
import { AppAvatar } from 'components/core/avatar'
import { AvatarSize } from 'components/core/avatar/AvatarSizes'
import { C1DisplayNameSkeleton, DisplayNameDetails } from 'components/core/display-name-details'
import { MinimalAPIError } from 'components/core/errors/api'
import { useHasBuiltinRole, useHasRole } from 'components/no-ui/capability/index'
import { AppValue } from 'lib/actions/apps'
import { RoleID } from 'lib/constants/system'
import { routes } from 'lib/routes'
import { App, AppRef } from 'pbts/c1/api/app/v1/app'
import React, { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { appMultiGetApi } from 'redux_toolkit/apps'
import { getAppEntitlementApi } from 'redux_toolkit/entitlements'
import { AppDisplayNameTooltip } from './AppDisplayNameTooltip'

type displayNameDetailsProps = React.ComponentProps<typeof DisplayNameDetails>
export type AppDisplayNameProps = Omit<displayNameDetailsProps, 'avatar' | 'displayName' | 'isDeleted'> & {
    app: App
    avatarSize?: AvatarSize
    includeDetails?: boolean
    iconOnly?: boolean
    readOnly?: boolean
    showTooltip?: boolean
    parentApp?: AppValue
    maxWidth?: string
    avatarIsEditable?: boolean
}

export const AppDisplayName = ({
    app,
    includeDetails,
    link = routes.applications.detail(app.id),
    avatarSize = 'md',
    iconOnly = false,
    readOnly = false,
    showTooltip = false,
    parentApp,
    maxWidth,
    avatarIsEditable = false,
    ...rest
}: AppDisplayNameProps): React.ReactElement => {
    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)
    const isOwner = useHasRole(AppV1Appsowner)
    const canNavigateToApp = isReadOnlySuperAdmin || isOwner

    const details = app.appAccountId !== ''
        ? `${app.appAccountName} (${app.appAccountId})`
        : app.appAccountName

    return <AppDisplayNameTooltip
        app={app}
        parentApp={parentApp}
        disableHoverListener={!showTooltip || !isOwner}
    >
        <Box sx={{ maxWidth }}>
            <DisplayNameDetails
                avatar={<AppAvatar
                    appId={app.id}
                    appName={app.displayName}
                    size={avatarSize}
                    decorative={!iconOnly}
                    editable={avatarIsEditable}
                />
                }
                readOnly={!canNavigateToApp || readOnly}
                displayName={iconOnly ? '' : app.displayName}
                link={link}
                details={includeDetails && details}
                isDeleted={app.deletedAt !== undefined}
                identifier={app.isDirectory ? appDirectoryIdentifier() : undefined}
                maxWidth={maxWidth}
                {...rest}
            />
        </Box>
    </AppDisplayNameTooltip>
}

const appDirectoryIdentifier = (): C1ChipProps => {
    return {
        label: <FormattedMessage id="components_app_display_name_directory" defaultMessage="Directory" />,
        color: 'primary',
    }
}

export type AppDisplayNameWithFetchProps = Omit<AppDisplayNameProps, 'app'> & {
    appId: string
}

// Map AvatarSize to C1DisplayNameSkeleton size
const mapAvatarSizeToSkeletonSize = (size: AvatarSize = 'md'): 'sm' | 'md' | 'lg' => {
    if (size === 'xs') return 'sm'
    if (size === 'xl') return 'lg'
    return size
}

export const AppDisplayNameWithFetch = ({
    appId,
    avatarSize = 'md',
    ...appDisplayNameProps
}: AppDisplayNameWithFetchProps): React.ReactElement | null => {
    const ref = useMemo(() => AppRef.create({ id: appId }), [appId])
    const { data, error, isLoading } = appMultiGetApi.useMultiGet(ref)

    if (error) {
        const err = (
            <FormattedMessage id="component_api_error_application" defaultMessage="Error fetching application" />
        )
        return <MinimalAPIError err={err} />
    }

    if (isLoading || !data) {
        return <C1DisplayNameSkeleton size={mapAvatarSizeToSkeletonSize(avatarSize)} />
    }

    return <AppDisplayName app={data} avatarSize={avatarSize} {...appDisplayNameProps} />
}

export type AppDisplayNameWithEntitlementFetchProps = Omit<AppDisplayNameProps, 'app'> & {
    entitlementId: string
    appId: string
}

export const AppDisplayNameWithEntitlementFetch = ({
    entitlementId: id,
    appId,
    avatarSize = 'md',
    ...appDisplayNameProps
}: AppDisplayNameWithEntitlementFetchProps): React.ReactElement | null => {
    const { data, error, isLoading } = getAppEntitlementApi.useGet({ id, appId, expandMask: { paths: ['*'] } })

    if (error) {
        const err = (
            <FormattedMessage id="component_api_error_application" defaultMessage="Error fetching application" />
        )
        return <MinimalAPIError err={err} />
    }

    if (isLoading || !data || !data.app) {
        return <C1DisplayNameSkeleton size={mapAvatarSizeToSkeletonSize(avatarSize)} />
    }

    return <AppDisplayName app={data.app} avatarSize={avatarSize} {...appDisplayNameProps} />
}
